import * as React from "react";
import { cn } from "@/lib/utils";
 import { cva, type VariantProps } from "class-variance-authority";
import { Icon } from "./icon";

const inputVariants = cva(
  "peer block w-full rounded-md px-3 py-2 text-sm transition-all duration-200 ease-in-out disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default: [
          "border border-border hover:border-action",
          "focus:border-2 focus:border-action focus:outline-none",
          "placeholder:text-action",
        ],
        primary: [
          "border border-border hover:border-primary/50",
          "focus:border-2 focus:border-primary focus:outline-none",
          "placeholder:text-muted-foreground",
        ],
        secondary: [
          "border border-border hover:border-secondary/50",
          "focus:border-2 focus:border-secondary focus:outline-none",
          "placeholder:text-muted-foreground",
        ],
        info: [
          "border border-border hover:border-info/50",
          "focus:border-2 focus:border-info focus:outline-none",
          "placeholder:text-muted-foreground",
        ],
        success: [
          "border border-border hover:border-success/50",
          "focus:border-2 focus:border-success focus:outline-none",
          "placeholder:text-muted-foreground",
        ],
        warning: [
          "border border-border hover:border-warning/50",
          "focus:border-2 focus:border-warning focus:outline-none",
          "placeholder:text-muted-foreground",
        ],
        error: [
          "border border-error hover:border-error",
          "focus:border-2 focus:border-error focus:outline-none",
          "placeholder:text-error/60",
        ],
      },
      inputSize: {
        default: "min-h-[56px]",
        sm: "min-h-[44px] text-xs",
        lg: "min-h-[64px] text-base",
      },
    },
    defaultVariants: {
      variant: "default",
      inputSize: "default",
    },
  }
);

const labelVariants = cva(
  "pointer-events-none absolute left-3 origin-[0] transform text-sm transition-all duration-200 ease-in-out text-muted-foreground px-1",
  {
    variants: {
      variant: {
        default: [
          "peer-focus:text-action-hover peer-focus:scale-75 peer-focus:-translate-y-[26px] peer-focus:bg-white",
          "translate-y-0 scale-100",
          "peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100",
        ],
        primary: [
          "peer-focus:text-primary peer-focus:scale-75 peer-focus:-translate-y-[26px] peer-focus:bg-white",
          "translate-y-0 scale-100",
          "peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100",
        ],
        secondary: [
          "peer-focus:text-secondary peer-focus:scale-75 peer-focus:-translate-y-[26px] peer-focus:bg-white",
          "translate-y-0 scale-100",
          "peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100",
        ],
        info: [
          "peer-focus:text-info peer-focus:scale-75 peer-focus:-translate-y-[26px] peer-focus:bg-white",
          "translate-y-0 scale-100",
          "peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100",
        ],
        success: [
          "peer-focus:text-success peer-focus:scale-75 peer-focus:-translate-y-[26px] peer-focus:bg-white",
          "translate-y-0 scale-100",
          "peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100",
        ],
        warning: [
          "peer-focus:text-warning peer-focus:scale-75 peer-focus:-translate-y-[26px] peer-focus:bg-white",
          "translate-y-0 scale-100",
          "peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100",
        ],
        error: [
          "peer-focus:text-error peer-focus:scale-75 peer-focus:-translate-y-[26px] peer-focus:bg-white",
          "translate-y-0 scale-100",
          "peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100",
        ],
      },
      inputSize: {
        default: "top-4",
        sm: "top-3",
        lg: "top-5",
      },
    },
    defaultVariants: {
      variant: "default",
      inputSize: "default",
    },
  }
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  startIcon?: string;
  endIcon?: string;
  title?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = "text", id, placeholder, title, value, defaultValue, startIcon, endIcon, variant, inputSize, ...props }, ref) => {
    const [hasValue, setHasValue] = React.useState(
      Boolean(defaultValue) || Boolean(value)
    );

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setHasValue(event.target.value !== "");
      if (props.onChange !== undefined) {
        props.onChange(event);
      }
    };

    React.useEffect(() => {
      if (value !== undefined) {
        setHasValue(value !== "");
      }
    }, [value]);

    return (
      <div className="relative w-full">
        {startIcon && (
          <Icon
            icon={startIcon}
            className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50"
          />
        )}
        <input
          type={type}
          id={id}
          className={cn(
            inputVariants({ variant, inputSize }),
            startIcon && "pl-10",
            endIcon && "pr-10",
            "border-2 bg-white text-foreground",
            className
          )}
          ref={ref}
          onChange={handleChange}
          placeholder={placeholder}
          value={value}
          defaultValue={defaultValue}
          {...props}
        />
        {endIcon && (
          <Icon
            icon={endIcon}
            className="absolute right-3 top-1/2 -translate-y-1/2 opacity-50"
          />
        )}
        <label
          htmlFor={id}
          className={cn(
            labelVariants({ variant, inputSize }),
            hasValue && "-translate-y-[26px] scale-75 bg-white",
            startIcon && "left-10"
          )}
        >
          {title}
        </label>
      </div>
    );
  }
);

Input.displayName = "Input";

export { Input };
